import { pick, range } from "lodash";
import { jfetch, snakeCaseKeys, snakeCaseKeysDeep } from "../utils";
import { getUserRecord, normalizeUser, getUser } from "./user";
import { createContext } from "react";
import {
  getAttributes,
  normalizeRecord,
  normalizeApiArrayResponse,
} from "./api_utils";

export function normalizeOrg(orgData: IOrganizationRecord): IOrganization {
  return normalizeRecord(orgData);
}

/**
 * Saves org data from a record
 * @deprecated Use normalized objects and saveOrg() instead
 * @param org
 */
export function saveOrgData(org: IOrganizationRecord) {
  const organization = snakeCaseKeys(
    pick(org.attributes, ["name", "logo", "settings", "onboardingStepsData"])
  );

  return jfetch.put<IOrganizationApiUpdateResponse>(
    `/organizations/${org.id}`,
    {
      organization,
    }
  );
}

/**
 * Saves org data from a normalized object
 * @param org
 */
export function saveOrg(org: IOrganization) {
  const organization = snakeCaseKeys(getAttributes(org));

  return jfetch.put<IOrganizationApiUpdateResponse>(
    `/organizations/${org.id}`,
    {
      organization,
    }
  );
}

export function getOrg(orgId: string | number) {
  return jfetch
    .get<IJsonApiResponse<IOrganizationRecord>>(`/organizations/${orgId}`)
    .then(resp => normalizeOrg(resp.data));
}

/**
 * gets users of an org
 * @param orgId
 * @param options - Options object
 * @param options.include - User relationships to include in response
 */
export function getOrgUsers(
  orgId: string | number,
  { include = [] }: { include?: string[] } = {}
) {
  return jfetch
    .get(`/organizations/${orgId}/users`, { include })
    .then(normalizeApiArrayResponse);
}

export function getOrgOwner(org: IOrganization) {
  return getUser(org.owner.id);
}

export function getAvailableManagersForOrg(
  orgId: string | number
): Promise<IUser[]> {
  return jfetch
    .get<IJsonApiResponse<IUserRecord[]>>(
      `/organizations/${orgId}/available_managers`
    )
    .then(resp => resp.data.map(normalizeUser));
}

export function getAllOrgs({ include = undefined } = {}) {
  return jfetch
    .get<IJsonApiResponse<IOrganizationRecord[]>>("/organizations", { include })
    .then(normalizeApiArrayResponse);
}

interface IAllOrgsPaginatedResponse {
  organizations: IJsonApiResponse;
  totalPages: number;
}

export function getAllOrgsPaginated({
  include = undefined,
  pagenum = 1,
  per = 25,
  search = null,
} = {}) {
  return jfetch.get<IAllOrgsPaginatedResponse>("/organizations", {
    include,
    pagenum: pagenum,
    per: per,
    search: search,
  });

  // const firstResponse = await jfetch.get<IAllOrgsPaginatedResponse>(
  //   "/organizations",
  //   {
  //     include,
  //     pagenum: pagenum,
  //     per: per,
  //     search: search,
  //   }
  // );

  // if (firstResponse.pages.last) {
  //   return normalizeApiArrayResponse(firstResponse.organizations);
  // } else {
  //   const pageCount = firstResponse.pages.total;
  //   const remainingPageNumbers = range(2, pageCount + 1);
  //   const remainingPages = await Promise.all(
  //     remainingPageNumbers.map(pagenum =>
  //       jfetch.get<IAllOrgsPaginatedResponse>("/organizations", {
  //         include,
  //         paginate: true,
  //         page: pagenum,
  //       })
  //     )
  //   );

  //   const remainingOrgs: {
  //     data: IJsonApiRecord[];
  //     included: IJsonApiRecord[];
  //   } = remainingPages.reduce(
  //     (acc, cv) => ({
  //       data: [...acc.data, ...cv.organizations.data],
  //       included: [...acc.included, ...cv.organizations.included],
  //     }),
  //     { data: [], included: [] }
  //   );

  //   const synthesizedResponse = {
  //     data: [...firstResponse.organizations.data, ...remainingOrgs.data],
  //     included: [
  //       ...firstResponse.organizations.included,
  //       ...remainingOrgs.included,
  //     ],
  //   } as IJsonApiResponse;

  //   return normalizeApiArrayResponse(synthesizedResponse);
  // }
}

interface IOrgCreationData {
  name: string;
  cwRecid: string;
  cwCompanyid: string;
  ownerName: string;
  ownerEmail: string;
  ownerTitle: string;
  membershipType: "classic" | "premium" | "legacy" | "a_la_carte";
  skipOnboarding: boolean;
  purchasedStrategySession: boolean;
  checkedSteps: Array<string>;
  pgroup: number;
}

export function createOrg(data: IOrgCreationData) {
  return jfetch.post<{ organization: IOrganizationRecord; owner: IUserRecord }>(
    `/organizations`,
    snakeCaseKeys(data)
  );
}

/**
 * Updates owner, pass in organization and new owner's user Id
 * @param org
 * @param ownerId
 */
export function updateOwner(
  org: IOrganization,
  ownerId: { ownerId: string; orgId: number | null }
) {
  return jfetch.post<IOrganizationApiUpdateResponse>(
    `/organizations/${org.id}/update_owner`,
    snakeCaseKeys(ownerId)
  );
}

/**
 * Updates member information
 * @param orgId
 * @param memberValue
 */
export function updateCWMember(orgId: string, memberValue: number) {
  return jfetch.put(`/organizations/${orgId}/cw_member/${memberValue}`, null);
}

/**
 * Disables organization
 * @param orgId
 */
export const disableOrg = (orgId: number | string) => {
  return jfetch.get(`/organizations/${orgId}/disable_organization`);
};



// WEBPACK FOOTER //
// ./app/javascript/libs/organization.ts